import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, BookOpen, Upload, Trash2, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";
const CATEGORIES = ["Recovery Guide", "Mobility Guide", "Nutrition", "Educational", "Evaluation PDF", "Other"];

export default function ResourcesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", category: "Educational" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/resources"); setItems(data); }
    catch { toast.error("Failed to load resources."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/resources?title=${encodeURIComponent(form.title || file.name)}&category=${encodeURIComponent(form.category)}`, fd);
      toast.success("Resource uploaded.");
      setForm({ title: "", category: "Educational" }); setFile(null);
      e.target.reset?.(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/resources/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="resources-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-2">Resources</h1>
      <p className="text-white/40 text-sm mb-8">Guides and documents shared with all Academy athletes.</p>

      <form onSubmit={upload} className="bg-[#121212] border border-white/10 p-6 mb-8 grid sm:grid-cols-3 gap-4" data-testid="resource-form">
        <div><label className={labelCls}>Title</label><input data-testid="res-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Recovery protocol…" /></div>
        <div><label className={labelCls}>Category</label>
          <select data-testid="res-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </div>
        <div><label className={labelCls}>File</label><input data-testid="res-file" type="file" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm text-white/70 file:mr-3 file:border-0 file:bg-white file:text-black file:px-4 file:py-2 file:font-bold file:uppercase file:text-xs file:tracking-widest" /></div>
        <div className="sm:col-span-3"><button data-testid="res-submit" disabled={busy} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Resource</button></div>
      </form>

      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : items.length === 0 ? <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="resources-empty">No resources uploaded yet.</div>
        : (
          <div className="space-y-2" data-testid="resources-list">
            {items.map((r) => (
              <div key={r.id} data-testid={`resource-${r.id}`} className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0"><BookOpen size={18} className="text-white/50 shrink-0" /><div className="min-w-0"><p className="font-bold truncate">{r.title}</p><p className="text-white/40 text-xs">{r.category}</p></div></div>
                <div className="flex items-center gap-3 shrink-0">
                  <a href={`${BACKEND}/api/resources/${r.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><ExternalLink size={13} /> Open</a>
                  <button data-testid={`resource-del-${r.id}`} onClick={() => del(r.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
